import Vue from "vue";
import Vuex from "vuex";
import files from "@/store/modules/files";
import users from "@/store/modules/users";
import alerts from "@/store/modules/alerts";
import logs from "@/store/modules/logs";
import auth from "@/store/modules/auth";
import oauth from "@/store/modules/oauth";
import relays from "@/store/modules/relays";
import boxes from "@/store/modules/boxes";
import keys from "@/store/modules/keys";
import locations from "@/store/modules/locations";
import tags from "@/store/modules/tags";
import companies from "@/store/modules/companies";
import maps from "@/store/modules/maps";
import payments from "@/store/modules/payments";
import bookings from "@/store/modules/bookings";
import categories from "@/store/modules/categories";
import rights from "@/store/modules/rights";
import orders from "@/store/modules/orders";
import lots from "@/store/modules/lots";
import keyGroups from "@/store/modules/keyGroups";
import userGroups from "@/store/modules/userGroups";
import banner from "@/store/modules/banner";
import keyManagements from "@/store/modules/keyManagements";
import keyAccesses from "@/store/modules/keyAccesses";
import connectorConnectedBoxes from "@/store/modules/connectors/connectedBoxes";
import connectorPappers from "@/store/modules/connectors/pappers";
import externalUrmet from "@/store/modules/connectors/urmet";
import context, { IContext } from "@/store/modules/context";
import sizes from "@/store/modules/sizes";
import badgeNfc from "@/store/modules/badgeNfc";
import dashboards from "@/store/modules/dashboards";
import widgets from "@/store/modules/widgets";
import buildings from "@/store/modules/buildings";
import missionOrders from "@/store/modules/missionOrders";
import options from "@/store/modules/options";
import nav from "@/store/modules/nav";
import nonTransferables from "@/store/modules/nonTransferables";
import notifications from "@/store/modules/notifications";
import buildingOrders from "@/store/modules/buildingOrders";
import buildingTransfers from "@/store/modules/buildingTransfers";
import {
  hasAcceptedCgu,
  isAdmin,
  isDemo,
  userCompaniesIds,
} from "@/services/users";
import store from "store2";
import { canUseFeature } from "@/services/companies";
import { isBookedKeyAvailable } from "@/services/bookings";
import adminSubModules from "@/store/modules/admin";
import publicSubModules from "@/store/modules/public";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    currentUser: undefined as any,
    companiesList: store.get("companiesList") || [],
    envCompany: store.get("envCompany") || null,
    loginType: "",
    networkError: false,
    importedUsers: [],
    baseURL: "",
    version: "",
    mobileMenuOpen: false,
    searchQuery: "",
    isMobile: false,
    isPwa: false,
    browserVersion: "",
    mylobyEnv: "",
  },
  mutations: {
    currentUser(state, user) {
      if (user) {
        state.currentUser = user;
      } else {
        state.currentUser = undefined;
      }
    },
    envCompany(state, value) {
      if (value) {
        state.envCompany = value;
      } else {
        state.envCompany = null;
      }
      store.set("envCompany", value);
    },
    companiesList(state, value) {
      if (value) {
        state.companiesList = value;
      } else {
        state.companiesList = [];
      }
      store.set("companiesList", value);
    },
    loginType(state, loginType) {
      state.loginType = loginType || "default";
    },
    networkError(state, networkError) {
      state.networkError = networkError;
    },
    importedUsers(state, users) {
      state.importedUsers = users;
    },
    setBaseURL(state, baseURL) {
      state.baseURL = baseURL;
    },
    setVersion(state, version) {
      state.version = version;
    },
    setMylobyEnv(state, mylobyEnv: string) {
      state.mylobyEnv = mylobyEnv;
    },
    mobileMenu(state, hide = false) {
      if (hide) {
        state.mobileMenuOpen = false;
      } else {
        state.mobileMenuOpen = !state.mobileMenuOpen;
      }
    },
    updateSearch(state, query) {
      state.searchQuery = query;
    },
    dispatchIsMobile(state, isMobile) {
      state.isMobile = isMobile;
    },
    dispatchIsPwa(state, isPwa) {
      state.isPwa = isPwa;
    },
    dispatchBrowserVersion(state, browserVersion) {
      state.browserVersion = browserVersion;
    },
  },
  getters: {
    // Env company
    companiesList: (state): any[] => state.companiesList,
    envCompany: (state): any|null => state.envCompany || null,
    envCompanyId: (state): number|null => state.envCompany?.id || null,
    canUseFeature: (state) => (feature: string) => canUseFeature(state.envCompany, feature),
    // Users
    currentUser: (state): any => state.currentUser,
    networkError: (state): any => state.networkError,
    isAdmin: (state): boolean => isAdmin(state.currentUser),
    isDemo: (state): boolean => isDemo(state.currentUser),
    hasAcceptedCgu: (state): boolean => hasAcceptedCgu(state.currentUser),
    userCompaniesIds: (state) => (right?: string) => userCompaniesIds(state.currentUser, right),
    // Rights
    canDoInEnvCompany: (state, getters, rootState, rootGetters) => (right: string | null) => {
      return state.envCompany?.id &&
        !state.envCompany.blockedAt &&
        rootGetters["oauth/isTokenCompany"](state.envCompany?.id) &&
        rootGetters["oauth/hasPermission"](right);
    },
    // Other
    loginType: (state): any => state.loginType,
    isMobileMenuOpen: (state): boolean => state.mobileMenuOpen,
    getSearchQuery: (state): string => state.searchQuery,
    isMobile: (state): boolean => state.isMobile,
    isPwa: (state): boolean => state.isPwa,
    browserVersion: (state): string => state.browserVersion,
    context: (state): IContext|null => (state as any).context.context,
    mylobyEnv: (state): string => state.mylobyEnv,
    isBookedKeyAvailable: (state) => (key: any): boolean => isBookedKeyAvailable(state.currentUser, key),
  },
  actions: {
    toggleMobileMenu({ commit }) {
      commit("mobileMenu");
    },
    async changeEnvCompanyId({ commit }, companyId: number|null) {
      if (companyId !== this.getters.envCompanyId) {
        if (companyId && !this.getters.isAdmin) {
          const companies: any[] = this.state.companiesList;
          let targetCompany = companies.find((c) => c.id === companyId);
          // If it's an external company
          if (!targetCompany) {
            // Get info
            targetCompany = await this.dispatch("companies/readById", { id: companyId });
            // Add to companies list
            companies.unshift(targetCompany);
            commit("companiesList", companies);
          }
          // Update env company
          commit("envCompany", targetCompany);
          await this.dispatch("auth/afterLogin", {});
        } else {
          commit("envCompany", null);
        }
      }
    },
    async autoSelectEnvCompany({ commit, dispatch, getters }) {
      const availableCompanies = getters.companiesList.filter((c) => !c.blockedAt);
      const currentCompany = availableCompanies.find((c) => c.id === getters.envCompany?.id);
      if (currentCompany) {
        // Refresh env company data with newly fetched company
        commit("envCompany", currentCompany);
      } else {
        // Auto select company
        const autoSelectedCompany = availableCompanies.length ? availableCompanies[0] : null;
        commit("envCompany", autoSelectedCompany);
      }
    },
  },
  modules: {
    auth,
    oauth,
    admin: adminSubModules,
    public: publicSubModules,
    // front-end modules
    alerts,
    banner,
    // other 'authenticated' modules
    files,
    users,
    logs,
    relays,
    boxes,
    keys,
    locations,
    tags,
    companies,
    maps,
    payments,
    bookings,
    rights,
    orders,
    lots,
    keyGroups,
    userGroups,
    keyManagements,
    keyAccesses,
    connectorConnectedBoxes,
    context,
    sizes,
    badgeNfc,
    dashboards,
    widgets,
    buildings,
    missionOrders,
    options,
    nav,
    connectorPappers,
    nonTransferables,
    categories,
    notifications,
    buildingOrders,
    externalUrmet,
    buildingTransfers,
  },
});
